import { supabase } from "@/integrations/supabase/client";
import { Profile } from "./types";
import { updateProfileCache } from "./profileCache";
import { createLogger } from "../utils/loggingUtils";
import { toast } from "sonner";

const logger = createLogger('createDefaultProfile');

export const createDefaultProfile = async (userId: string): Promise<Profile | null> => {
  try {
    logger.info("Creating default profile for user:", userId);
    
    // Get user's email to use as initial username if available
    const { data: userData } = await supabase.auth.getUser();
    let initialUsername = null;
    
    if (userData?.user?.email) {
      // Use the part before @ as the username
      initialUsername = userData.user.email.split('@')[0];
      logger.info("Using initial username from email:", initialUsername);
    }
    
    const defaultProfile = {
      id: userId,
      username: initialUsername,
      display_username: null,
      lab: null,
      university: null,
      location: null,
      city: null,
      country: null,
      research_interests: null,
      expertise: null,
      linkedin_url: null,
      website_url: null,
      credit_balance: 0,
      is_admin: false, // SECURITY: Never auto-grant admin privileges
      email_notifications: true,
      notify_swap_offers: true,
      notify_swap_responses: true,
      notify_messages: true,
      daily_digest_email: true,
      weekly_digest_email: true,
      university_only_swaps: true
    };
    
    // Try to create a default profile
    const { data: newProfile, error: createError } = await supabase
      .from("profiles")
      .insert(defaultProfile)
      .select()
      .single();
      
    if (createError) {
      logger.error("Error creating default profile:", createError);
      
      // Last chance: try one more time with a delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const { data: retryProfile, error: retryError } = await supabase
        .from("profiles")
        .insert(defaultProfile)
        .select()
        .single();
        
      if (retryError) {
        logger.error("Error creating profile on retry:", retryError);
        toast.error("Failed to create user profile");
        return null;
      }
      
      // Update cache with the newly created profile
      updateProfileCache(userId, retryProfile as Profile);
      logger.info("Successfully created profile on retry:", retryProfile);
      return retryProfile as Profile;
    }
    
    // Update cache with the new profile
    updateProfileCache(userId, newProfile as Profile);
    
    logger.info("Successfully created default profile:", newProfile);
    return newProfile as Profile;
  } catch (createException) {
    logger.error("Exception creating default profile:", createException);
    toast.error("Failed to create user profile");
    return null;
  }
};